import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../../App";
import { SmilePlus, Meh, Frown, TrendingUp, TrendingDown, ThumbsUp, ThumbsDown, Send, Sparkles, Copy, Check, ChevronDown, ChevronUp, Building2, User, Calendar, Loader2, StickyNote, CheckCircle2, AlertCircle, Clock, Save, BarChart3 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";

export const ScoreGauge = ({ score, label, maxScore = 100, color = "navy" }) => {
  const percentage = maxScore === 100 ? score : ((score + 100) / 200) * 100;
  const colorClasses = {
    navy: "stroke-[#425B76]",
    emerald: "stroke-emerald-500",
    amber: "stroke-amber-500"
  };
  
  return (
    <div className="text-center">
      <div className="relative inline-flex items-center justify-center">
        <svg className="w-40 h-40 transform -rotate-90">
          <circle cx="80" cy="80" r="70" stroke="#e2e8f0" strokeWidth="10" fill="none" />
          <circle
            cx="80" cy="80" r="70"
            className={colorClasses[color]}
            strokeWidth="10"
            fill="none"
            strokeDasharray={`${(percentage / 100) * 439.6} 439.6`}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center flex-col">
          <span className="text-4xl font-bold text-slate-900">{score}</span>
          <span className="text-slate-500 text-sm mt-1">{label}</span>
        </div>
      </div>
    </div>
  );
};

// NPS Breakdown Card Component - Similar to the reference screenshot
export const NPSBreakdownCard = ({ title, score, total, promoters, passives, detractors }) => {
  const totalResponses = promoters + passives + detractors;
  const promoterPct = totalResponses > 0 ? (promoters / totalResponses) * 100 : 0;
  const passivePct = totalResponses > 0 ? (passives / totalResponses) * 100 : 0;
  const detractorPct = totalResponses > 0 ? (detractors / totalResponses) * 100 : 0;
  
  // Get score color based on NPS value
  const getScoreColor = (nps) => {
    if (nps >= 50) return "text-emerald-500";
    if (nps >= 30) return "text-emerald-600";
    if (nps >= 0) return "text-amber-500";
    return "text-red-500";
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-lg font-semibold text-slate-700">{title}</span>
          <span className="text-sm text-slate-500">{total} responses</span>
        </div>
        
        {/* Score */}
        <div className={`text-5xl font-bold mb-6 ${getScoreColor(score)}`}>
          {score}
        </div>
        
        {/* Stacked Bar */}
        <div className="h-3 rounded-full overflow-hidden flex mb-4">
          <div 
            className="bg-emerald-500 transition-all duration-500" 
            style={{ width: `${promoterPct}%` }}
          />
          <div 
            className="bg-amber-400 transition-all duration-500" 
            style={{ width: `${passivePct}%` }}
          />
          <div 
            className="bg-red-500 transition-all duration-500" 
            style={{ width: `${detractorPct}%` }}
          />
        </div>
        
        {/* Legend */}
        <div className="flex justify-between text-sm">
          <span className="text-emerald-600">{promoters} promoters</span>
          <span className="text-amber-600">{passives} passive</span>
          <span className="text-red-500">{detractors} detractors</span>
        </div>
      </CardContent>
    </Card>
  );
};

export const ComparisonBadge = ({ change, label }) => {
  if (change === undefined || change === null) return null;
  const isPositive = change >= 0;
  return (
    <div className={`flex items-center gap-1 text-sm ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
      {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
      <span>{isPositive ? '+' : ''}{change} vs {label}</span>
    </div>
  );
};

// Get bar color based on score - gradient from red to yellow to green
export const getScoreColor = (score, surveyType) => {
  if (surveyType === "nps") {
    // NPS ranges from -100 to +100
    if (score >= 50) return "#10b981"; // emerald-500 (excellent)
    if (score >= 30) return "#22c55e"; // green-500 (good)
    if (score >= 0) return "#eab308";  // yellow-500 (ok)
    if (score >= -20) return "#f97316"; // orange-500 (needs improvement)
    return "#ef4444"; // red-500 (poor)
  } else {
    // CSAT is 0-100%
    if (score >= 80) return "#10b981"; // emerald-500
    if (score >= 60) return "#22c55e"; // green-500
    if (score >= 40) return "#eab308"; // yellow-500
    if (score >= 20) return "#f97316"; // orange-500
    return "#ef4444"; // red-500
  }
};

export const TrendCharts = ({ trends, surveyType }) => {
  if (!trends) return null;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="font-semibold text-slate-900">{label}</p>
          <p className="text-lg font-bold" style={{ color: getScoreColor(data.score, surveyType) }}>
            Score: {data.score}{surveyType === "csat" ? "%" : ""}
          </p>
          <p className="text-sm text-slate-500">
            {data.total} responses
          </p>
          {surveyType === "nps" && (
            <div className="text-xs text-slate-400 mt-1">
              <span className="text-emerald-600">P: {data.promoters}</span> | 
              <span className="text-amber-600"> N: {data.passives}</span> | 
              <span className="text-red-500"> D: {data.detractors}</span>
            </div>
          )}
          {surveyType === "csat" && (
            <div className="text-xs text-slate-400 mt-1">
              <span className="text-emerald-600">+: {data.positive}</span> | 
              <span className="text-amber-600"> ~: {data.neutral}</span> | 
              <span className="text-red-500"> -: {data.negative}</span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
      {/* Month over Month Chart */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#425B76]" />
              Month over Month
            </CardTitle>
            {trends.mom_change !== null && (
              <Badge className={`${trends.mom_change >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {trends.mom_change >= 0 ? '+' : ''}{trends.mom_change} MoM
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trends.monthly} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis 
                dataKey="month_short" 
                tick={{ fontSize: 12, fill: '#64748b' }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
                domain={surveyType === "nps" ? [-100, 100] : [0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="score" 
                radius={[4, 4, 0, 0]}
                maxBarSize={50}
              >
                {trends.monthly.map((entry, index) => (
                  <Cell 
                    key={`cell-${entry.month_short ?? index}`} 
                    fill={getScoreColor(entry.score, surveyType)}
                    className="transition-all duration-300 hover:opacity-80"
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Year over Year Chart */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#425B76]" />
              Year over Year
            </CardTitle>
            {trends.yoy_change !== null && (
              <Badge className={`${trends.yoy_change >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {trends.yoy_change >= 0 ? '+' : ''}{trends.yoy_change} YoY
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trends.yearly} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis 
                dataKey="year" 
                tick={{ fontSize: 12, fill: '#64748b' }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
                domain={surveyType === "nps" ? [-100, 100] : [0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="score" 
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
              >
                {trends.yearly.map((entry, index) => (
                  <Cell 
                    key={`cell-${entry.year ?? index}`} 
                    fill={getScoreColor(entry.score, surveyType)}
                    className="transition-all duration-300 hover:opacity-80"
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export const FeedbackCard = ({ submission, surveyType, onDraftResponse, feedbackNotes, onNotesUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [copied, setCopied] = useState(false);
  
  // Notes state
  const existingNote = feedbackNotes?.[submission.id] || {};
  const [notes, setNotes] = useState(existingNote.notes || "");
  const [responseStatus, setResponseStatus] = useState(existingNote.response_status || "needs_response");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesChanged, setNotesChanged] = useState(false);

  // Update local state when feedbackNotes changes
  useEffect(() => {
    const note = feedbackNotes?.[submission.id];
    if (note) {
      setNotes(note.notes || "");
      setResponseStatus(note.response_status || "needs_response");
    }
  }, [feedbackNotes, submission.id]);

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(
        `${API}/feedback/notes/${submission.id}?survey_type=${surveyType}`,
        { notes, response_status: responseStatus },
        { withCredentials: true }
      );
      setNotesChanged(false);
      toast.success("Notes saved");
      if (onNotesUpdate) onNotesUpdate();
    } catch (error) {
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setResponseStatus(newStatus);
    try {
      await axios.post(
        `${API}/feedback/notes/${submission.id}/status`,
        { status: newStatus, survey_type: surveyType },
        { withCredentials: true }
      );
      if (onNotesUpdate) onNotesUpdate();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleGenerateDraft = async () => {
    setDraftLoading(true);
    try {
      const response = await axios.post(
        `${API}/surveys/draft-response`,
        {
          submission_id: submission.id,
          content: submission.content,
          sentiment: submission.sentiment,
          contact_name: submission.contact_name,
          survey_type: surveyType,
          rating: submission.rating
        },
        { withCredentials: true }
      );
      setDraft(response.data.draft);
      setResponseText(response.data.draft);
      toast.success("Draft generated");
    } catch (error) {
      toast.error("Failed to generate draft");
    } finally {
      setDraftLoading(false);
    }
  };

  const handleSendResponse = async () => {
    if (!responseText.trim()) {
      toast.error("Please enter a response");
      return;
    }
    if (!submission.contact_email) {
      toast.error("No contact email available for this submission");
      return;
    }
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/surveys/send-response`,
        {
          submission_id: submission.id,
          contact_id: submission.contact_id,
          contact_email: submission.contact_email,
          response: responseText,
          survey_type: surveyType
        },
        { withCredentials: true }
      );
      
      if (response.data.requires_manual_send) {
        toast.info("Response logged in HubSpot - please send manually from HubSpot inbox");
      } else {
        toast.success("Email sent successfully!");
        // Update status to responded
        handleStatusChange("responded");
      }
      setExpanded(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send response");
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(responseText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getSentimentIcon = () => {
    if (surveyType === "NPS") {
      if (submission.sentiment === "Promoter") return <ThumbsUp className="w-4 h-4 text-emerald-600" />;
      if (submission.sentiment === "Detractor") return <ThumbsDown className="w-4 h-4 text-red-600" />;
      return <Meh className="w-4 h-4 text-amber-600" />;
    }
    if (submission.sentiment === "positive") return <SmilePlus className="w-4 h-4 text-emerald-600" />;
    if (submission.sentiment === "negative") return <Frown className="w-4 h-4 text-red-600" />;
    return <Meh className="w-4 h-4 text-amber-600" />;
  };

  const getSentimentBg = () => {
    if (surveyType === "NPS") {
      if (submission.sentiment === "Promoter") return "bg-emerald-100";
      if (submission.sentiment === "Detractor") return "bg-red-100";
      return "bg-amber-100";
    }
    if (submission.sentiment === "positive") return "bg-emerald-100";
    if (submission.sentiment === "negative") return "bg-red-100";
    return "bg-amber-100";
  };

  const getStatusBadge = () => {
    if (responseStatus === "responded") {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Responded
        </Badge>
      );
    }
    if (responseStatus === "no_action_needed") {
      return (
        <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
          <Check className="w-3 h-3 mr-1" />
          No Action Needed
        </Badge>
      );
    }
    return (
      <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
        <Clock className="w-3 h-3 mr-1" />
        Needs Response
      </Badge>
    );
  };

  return (
    <motion.div
      layout
      className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden"
    >
      <div
        className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${getSentimentBg()} flex items-center justify-center flex-shrink-0`}>
            {getSentimentIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-medium text-slate-900">{submission.contact_name}</span>
              {getStatusBadge()}
              {submission.user_type && (
                <Badge variant="outline" className="text-xs border-slate-200">
                  <User className="w-3 h-3 mr-1" />
                  {submission.user_type}
                </Badge>
              )}
              {submission.company && (
                <Badge variant="outline" className="text-xs border-slate-200">
                  <Building2 className="w-3 h-3 mr-1" />
                  {submission.company}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-500 mb-2">
              {surveyType === "NPS" && submission.rating !== null && (
                <span className="font-medium">Score: {submission.rating}/10</span>
              )}
              <span className="capitalize">{submission.sentiment_raw || submission.sentiment}</span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(submission.date).toLocaleDateString()}
              </span>
            </div>
            {submission.content && (
              <p className="text-sm text-slate-600 line-clamp-2">{submission.content}</p>
            )}
          </div>
          <Button variant="ghost" size="icon" className="text-slate-400">
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-slate-100 pt-4 space-y-4">
              {/* Feedback Content */}
              {submission.content && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-700">{submission.content}</p>
                </div>
              )}

              {/* Response Status Section */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Response Status
                  </span>
                  <Select value={responseStatus} onValueChange={handleStatusChange}>
                    <SelectTrigger className="w-[180px] h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="needs_response">
                        <span className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-amber-600" />
                          Needs Response
                        </span>
                      </SelectItem>
                      <SelectItem value="responded">
                        <span className="flex items-center gap-2">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          Responded
                        </span>
                      </SelectItem>
                      <SelectItem value="no_action_needed">
                        <span className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-slate-600" />
                          No Action Needed
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Team Notes */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <StickyNote className="w-4 h-4" />
                    Team Notes
                  </label>
                  <Textarea
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      setNotesChanged(true);
                    }}
                    placeholder="Add notes about this feedback (e.g., actions taken, follow-up needed)..."
                    className="min-h-[80px] bg-white text-sm"
                  />
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-slate-500">
                      {existingNote.last_updated_by && (
                        <span>
                          Last updated by {existingNote.last_updated_by} on{" "}
                          {new Date(existingNote.last_updated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={handleSaveNotes}
                      disabled={savingNotes || !notesChanged}
                      className="gap-2 bg-[#425B76] hover:bg-[#2D4156]"
                    >
                      {savingNotes ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Save className="w-3 h-3" />
                      )}
                      Save Notes
                    </Button>
                  </div>
                </div>
              </div>

              {/* Response Draft Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Draft Response</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleGenerateDraft}
                    disabled={draftLoading}
                    className="gap-2"
                  >
                    {draftLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    Generate AI Draft
                  </Button>
                </div>
                <Textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Write your response..."
                  className="min-h-[100px] bg-white"
                />
                <div className="flex items-center justify-between">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copyToClipboard}
                    disabled={!responseText}
                    className="gap-2"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                  <Button
                    onClick={handleSendResponse}
                    disabled={sending || !responseText.trim() || !submission.contact_email}
                    className="bg-[#425B76] hover:bg-[#2D4156] gap-2"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Send Email
                  </Button>
                </div>
                {!submission.contact_email && (
                  <p className="text-xs text-amber-600">No contact email available - cannot send directly</p>
                )}
                <p className="text-xs text-slate-400">Sends from support@talentdesk.io and logs in HubSpot</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
